import { ChatMessageDocument } from '@models/chat';
import { useState } from 'react';

const VIDEO_WIDTH = 500;

// Chrome/Firefox reject `<source type="video/quicktime">` even when the .mov
// is H.264 they could play — hinting `video/mp4` first lets them decode it.
function getVideoSourceTypes(mime: string): string[] {
  const lower = mime.toLowerCase();
  if (lower === 'video/quicktime' || lower === 'video/x-quicktime') {
    return ['video/mp4', mime];
  }
  if (lower === 'video/mp4') return [mime];
  return [mime, 'video/mp4'];
}

interface MessageVideoProps {
  message: ChatMessageDocument;
  onRightClick?: (ev: React.MouseEvent) => void;
}

export default function MessageVideo({
  message,
  onRightClick,
}: MessageVideoProps) {
  const [errored, setErrored] = useState(false);
  // Prefer the Firebase download URL — Bunny CDN drops Range support for
  // non-image assets, which makes seek/playback stall at 0:00.
  const src = message.download_url || message.content;
  if (!src) return null;
  const mime = message.mime_type ?? 'video/mp4';
  const sourceTypes = getVideoSourceTypes(mime);

  if (errored) {
    return (
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        className="flex max-w-[50rem] flex-col gap-[0.4rem] rounded-[1rem] border border-zinc-300 bg-zinc-50 p-[1.6rem] text-[1.4rem] text-zinc-700 hover:bg-zinc-100"
        onContextMenu={onRightClick}
      >
        <span className="font-semibold">Video can&apos;t play in browser</span>
        <span className="text-zinc-500">
          Tap to download and open in your video player.
        </span>
      </a>
    );
  }

  return (
    <video
      controls
      preload="metadata"
      playsInline
      width={VIDEO_WIDTH}
      poster={message.thumbnail || undefined}
      onContextMenu={onRightClick}
      onError={() => setErrored(true)}
      className="max-w-full rounded-[1rem] bg-black"
    >
      {sourceTypes.map((type) => (
        <source key={type} src={src} type={type} />
      ))}
    </video>
  );
}
